/**
 * ContactMe event dispatch — same event names as main project (frontendrealestate).
 * Emits both contactme:* (widget contract) and lead-* (main project EventListener).
 */
import type { ContactMeOpenDetail, ContactMeSubmitPayload } from '../types'

function getPropertyIdFromContext(context?: ContactMeOpenDetail['context']): string | undefined {
  if (!context || typeof context !== 'object') return undefined
  const id =
    (context as Record<string, unknown>).propertyId ??
    (context as Record<string, unknown>).property_id
  return id != null ? String(id) : undefined
}

function dispatch(name: string, detail?: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }))
  } catch {
    // ignore
  }
}

export function useContactMeEvents() {
  const emitOpened = (detail?: ContactMeOpenDetail) => {
    dispatch('contactme:opened', detail)
    const propertyId = getPropertyIdFromContext(detail?.context)
    if (propertyId) dispatch('lead-open', propertyId)
  }

  const emitClosed = () => {
    dispatch('contactme:closed')
  }

  const emitSubmitAttempt = (payload: ContactMeSubmitPayload) => {
    dispatch('contactme:submit-attempt', payload)
    const propertyId = getPropertyIdFromContext(payload.context)
    dispatch('lead-submit-attempt', propertyId ? { propertyId } : undefined)
  }

  const emitSubmitted = (payload: ContactMeSubmitPayload) => {
    dispatch('contactme:submitted', { payload, transport: 'graphql' })
    const propertyId = getPropertyIdFromContext(payload.context)
    dispatch('lead-submit-success', propertyId ? { propertyId } : undefined)
  }

  const emitError = (stage: string, error: string, payload?: ContactMeSubmitPayload) => {
    dispatch('contactme:error', { stage, error, payload })
    const propertyId = payload?.context ? getPropertyIdFromContext(payload.context) : undefined
    dispatch('lead-submit-error', { propertyId, code: error })
  }

  const emitInitialSubmitted = (payload: unknown) => {
    dispatch('contactme:initial-submitted', payload)
  }

  return {
    emitOpened,
    emitClosed,
    emitSubmitAttempt,
    emitSubmitted,
    emitError,
    emitInitialSubmitted,
  }
}
